import React, { useState, useRef, useEffect } from 'react';
import './Chat.css';
import { 
  Box, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ChatHistory, { ChatHistoryRef } from './ChatHistory';
import { useMsal } from '@azure/msal-react';
import { sendChatMessage } from '../Services/ChatService';
import { getPermittedDataSources } from '../Services/DataSourceService';
import { getPrompts } from '../Services/PromptService';
import { getConversationMessages, getConversations, Conversation } from '../Services/ChatHistoryService';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType } from 'docx';
import { saveAs } from 'file-saver';

// Types and interfaces
type ExecutionMode = 'Chat' | 'Memo Gen';
interface Company {
  id: number;
  company: string;
}
type Source = 'Company Know'; // | 'World Knowledge'; // Commented out - only Company Knowledge is available

interface Citation {
  id: string;
  title: string;
  source: string;
  url: string;
  snippet: string;
  citationNumber: number;
}

interface Prompt {
  id?: number;
  promptName: string;
  promptType: string;
}

interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp: Date;
  citations?: Citation[];
}

interface ChatRequest {
  question: string;
  selectedCompany: Company;
  executionMode: ExecutionMode;
  source: Source;
  memoPrompt?: string;
  memoPromptId?: number;
  files: File[];
  chatHistory: Array<{ role: string; content: string }>;
  conversationId?: number;
}

// Constants
const MAX_FILES = 5;
const ALLOWED_TYPES = '.pdf,.csv,.doc,.docx,.jpg,.jpeg,.png,.pptx,.xlsx,.xlsm';

const Chat: React.FC = () => {
  const { instance, accounts } = useMsal();
  
  // UI State
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileError, setFileError] = useState<string>('');
  const [companyError, setCompanyError] = useState<string>('');
  
  // Form State
  const [executionMode, setExecutionMode] = useState<ExecutionMode>('Chat');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedSource, setSelectedSource] = useState<Source>('Company Know');
  const [selectedMemoPrompt, setSelectedMemoPrompt] = useState<Prompt | null>(null);
  const [question, setQuestion] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isCompanyLocked, setIsCompanyLocked] = useState<boolean>(false);
  
  // Data State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<number | undefined>();
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatHistoryRef = useRef<ChatHistoryRef>(null);
  const initRef = useRef(false);
  const promptInitRef = useRef(false);

  // Data fetching functions
  const fetchCompanies = async () => {
    if (initRef.current || !accounts[0]) return;
    
    try {
      initRef.current = true;
      const response = await getPermittedDataSources(instance, accounts[0]);
      
      // Response should contain objects with id and company properties
      const companyData = response.map((item: { id: number; company: string }) => ({
        id: item.id,
        company: item.company
      })) as Company[];

      if (companyData.length > 0) {
        setCompanies(companyData);
        // Don't set a default company - let user select
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      initRef.current = false;
    }
  };

  const fetchPrompts = async () => {
    if (promptInitRef.current || !accounts[0]) return;
    
    try {
      promptInitRef.current = true;
      const response = await getPrompts(instance, accounts[0]);
      const userPrompts = response
        .filter((prompt: Prompt) => prompt.promptType === 'User');

      if (userPrompts.length > 0) {
        setPrompts(userPrompts);
        setSelectedMemoPrompt(userPrompts[0]);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      promptInitRef.current = false;
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchPrompts();
  }, []);

  // File handling functions
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const MAX_FILE_SIZE = 64 * 1024 * 1024; // 64MB in bytes
    const newFiles = Array.from(files);
    
    // Clear any previous errors
    setFileError('');
    
    // Check file sizes
    const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setFileError(`The following file(s) exceed the limit of 64MB: ${oversizedFiles.map(f => f.name).join(', ')}`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (selectedFiles.length + newFiles.length <= MAX_FILES) {
      setSelectedFiles(prev => [...prev, ...newFiles]);
    } else {
      setFileError(`You can only upload up to ${MAX_FILES} files.`);
    }
    
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Utility functions
  const scrollToBottom = () => {
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-messages');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 0);
  };

  const createChatRequest = (trimmedQuestion: string): ChatRequest | null => {
    if (!selectedCompany) return null;
    
    return {
      question: trimmedQuestion,
      selectedCompany,
      executionMode,
      source: selectedSource,
      memoPrompt: executionMode === 'Memo Gen' && selectedMemoPrompt ? selectedMemoPrompt.promptName : undefined,
      memoPromptId: executionMode === 'Memo Gen' && selectedMemoPrompt ? selectedMemoPrompt.id : undefined,
      files: selectedFiles,
      chatHistory: messages.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.text
      })),
      conversationId: currentConversationId
    };
  };

  const addMessageToChat = (text: string, isUser: boolean, citations?: Citation[]) => {
    const message: ChatMessage = {
      text,
      isUser,
      timestamp: new Date(),
      citations
    };
    console.log('Adding message to chat:', message);
    setMessages(prev => [...prev, message]);
  };

  // Handle conversation selection from chat history
  const handleConversationSelect = async (conversation: Conversation) => {
    try {
      setIsLoading(true);
      
      // Load messages for the selected conversation
      const historyMessages = await getConversationMessages(instance, accounts[0], conversation.id);
      
      // Convert history messages to chat messages
      const chatMessages: ChatMessage[] = historyMessages.map(msg => ({
        text: msg.content,
        isUser: msg.role === 'user',
        timestamp: new Date(msg.createdAt),
        citations: msg.citationsJson ? JSON.parse(msg.citationsJson) : undefined
      }));
      
      // Get the last message to load its options
      const lastMessage = historyMessages[historyMessages.length - 1];
      
      // Update state
      setMessages(chatMessages);
      setCurrentConversationId(conversation.id);
      
      // Find the company object by name
      const companyObj = companies.find(c => c.company === conversation.companyName);
      if (companyObj) {
        setSelectedCompany(companyObj);
      }
      
      // Load options from the last message if available, otherwise use defaults
      if (lastMessage) {
        setExecutionMode((lastMessage.executionMode as ExecutionMode) || 'Chat');
        setSelectedSource((lastMessage.knowledgeSource as Source) || 'Company Know');

        // Prefer prompt id from backend; fallback to name
        if (typeof (lastMessage as any).promptSettingsId === 'number') {
          const promptObj = prompts.find(p => p.id === (lastMessage as any).promptSettingsId);
          setSelectedMemoPrompt(promptObj || null);
        } else if ((lastMessage as any).memoPromptName) {
          const promptObj = prompts.find(p => p.promptName === (lastMessage as any).memoPromptName);
          setSelectedMemoPrompt(promptObj || null);
        } else {
          setSelectedMemoPrompt(null);
        }
      } else {
        // Use defaults if no messages exist
        setExecutionMode('Chat');
        setSelectedSource('Company Know');
        setSelectedMemoPrompt(null);
      }
      
      // Lock the company since this is an existing conversation
      setIsCompanyLocked(true);
      setChatStarted(true);
      
      // Scroll to bottom after loading messages
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
    } catch (error) {
      console.error('Error loading conversation:', error);
      addMessageToChat("Sorry, there was an error loading the conversation history.", false);
    } finally {
      setIsLoading(false);
    }
  };

  // Start a new chat
  const handleNewChat = () => {
    setMessages([]);
    setCurrentConversationId(undefined);
    setChatStarted(false);
    setQuestion('');
    setSelectedFiles([]);
    setFileError('');
    setCompanyError('');
    
    // Reset all form options
    setSelectedCompany(null);
    setExecutionMode('Chat');
    setSelectedSource('Company Know');
    setSelectedMemoPrompt(null);
    setIsCompanyLocked(false);
  };

  // Chat submission handler
  const handleSubmit = async () => {
    const trimmedQuestion = question.trim();

    // Validation: Check if company is selected
    if (!selectedCompany) {
      setCompanyError('Please select a company before sending your message.');
      return;
    }
    setCompanyError(''); // Clear error if company is selected

    setIsLoading(true);
    setChatStarted(true);

    const isNewConversation = !currentConversationId;
    const requestStartTime = new Date();

    // Add user's message only if there's actual content
    if (trimmedQuestion) {
      addMessageToChat(trimmedQuestion, true);
    }

    try {
      const chatRequest = createChatRequest(trimmedQuestion || ''); // Use empty string if no question
      if (!chatRequest) {
        addMessageToChat("Error: No company selected.", false);
        return;
      }
      const chatIter = sendChatMessage(instance, chatRequest, accounts[0]);
      
      // Process streaming response
      for await (const chunk of chatIter) {
        console.log('Received chunk:', chunk);
        console.log('Citations in chunk:', chunk.citations);
        
        if (chunk.text && chunk.isComplete) {
          addMessageToChat(chunk.text, false, chunk.citations);
          scrollToBottom();
        }
      }
    } catch (error) {
      console.error('Error getting response:', error);
      addMessageToChat("Sorry, there was an error processing your request. Please try again.", false);
    } finally {
      setIsLoading(false);
      setQuestion('');
      
      // Lock the company after the first message is sent
      if (!isCompanyLocked) {
        setIsCompanyLocked(true);
      }
      
      // Refresh chat history to show the new conversation
      if (chatHistoryRef.current) {
        chatHistoryRef.current.refreshConversations();
      }

      // If this was the first message of a new chat, resolve and persist the created conversation id
      if (isNewConversation && !currentConversationId) {
        try {
          const convos = await getConversations(instance, accounts[0]);
          // Backend returns conversations ordered by CreatedAt DESC
          const recentThresholdMs = 2 * 60 * 1000; // 2 minutes window
          const candidate = convos?.find(c => {
            const created = new Date(c.createdAt).getTime();
            return c.companyName === selectedCompany?.company && (requestStartTime.getTime() - created) <= recentThresholdMs;
          }) || convos?.[0];

          if (candidate) {
            setCurrentConversationId(candidate.id);
            setIsCompanyLocked(true);
          }
        } catch (e) {
          console.error('Failed to resolve newly created conversation id:', e);
        }
      }
    }
  };

  // Event handlers
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        handleSubmit();
      }
    }
  };

  // UI State for export error
  const [exportError, setExportError] = useState<string>('');

  // Export functionality
  const handleExportToWord = async () => {
    // Find the last system message (non-user message)
    const lastSystemMessage = messages.slice().reverse().find(msg => !msg.isUser);
    
    if (!lastSystemMessage) {
      setExportError('No system message found to export.');
      return;
    }
    setExportError(''); // Clear error if export proceeds

    try {
      // Helper function to clean markdown symbols from text
      const cleanMarkdownText = (text: string): string => {
        return text
          .replace(/`([^`]+)`/g, '$1')           // Remove backticks but keep code text
          .replace(/\*{1,3}([^*]+?)\*{1,3}/g, '$1')  // Remove all asterisk formatting
          .replace(/\*/g, '');                   // Remove any remaining asterisks
      };

      // Helper function to create table from rows
      const createWordTable = (tableRows: string[][]): any => {
        const wordTableRows = tableRows.map((row, rowIndex) => {
          return new TableRow({
            children: row.map(cell => {
              const cleanCell = cleanMarkdownText(cell);
              
              return new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: cleanCell,
                        bold: rowIndex === 0, // Make header row bold
                        size: 22,
                      }),
                    ],
                  }),
                ],
                width: {
                  size: Math.floor(100 / row.length), // Distribute width evenly
                  type: WidthType.PERCENTAGE,
                },
              });
            }),
          });
        });

        return new Table({
          rows: wordTableRows,
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
          borders: {
            top: { style: 'single', size: 1, color: '000000' },
            bottom: { style: 'single', size: 1, color: '000000' },
            left: { style: 'single', size: 1, color: '000000' },
            right: { style: 'single', size: 1, color: '000000' },
            insideHorizontal: { style: 'single', size: 1, color: '000000' },
            insideVertical: { style: 'single', size: 1, color: '000000' },
          },
        });
      };
      // Parse markdown content into paragraphs
      const parseMarkdownToWordElements = (markdownText: string) => {
        const lines = markdownText.split('\n');
        const elements: any[] = [];
        let inCodeBlock = false;
        let codeBlockContent: string[] = [];
        let currentListItems: string[] = [];
        let inTable = false;
        let tableRows: string[][] = [];

        // Helper function to process accumulated list items
        const processListItems = () => {
          if (currentListItems.length > 0) {
            currentListItems.forEach(item => {
              elements.push(new Paragraph({
                children: [
                  new TextRun({
                    text: item,
                    size: 24,
                  }),
                ],
                spacing: { before: 100, after: 100 },
              }));
            });
            currentListItems = [];
          }
        };

        // Helper function to process accumulated table rows
        const processTableRows = () => {
          if (tableRows.length > 0) {
            elements.push(createWordTable(tableRows));
            // Add spacing after table
            elements.push(new Paragraph({
              children: [new TextRun({ text: "", size: 24 })],
              spacing: { before: 200, after: 200 },
            }));
          }
          inTable = false;
          tableRows = [];
        };

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          
          // Handle code blocks
          if (line.trim().startsWith('```')) {
            if (inCodeBlock) {
              // End code block
              elements.push(new Paragraph({
                children: [
                  new TextRun({
                    text: codeBlockContent.join('\n'),
                    font: 'Courier New',
                    size: 20,
                  }),
                ],
                spacing: { before: 200, after: 200 },
                border: {
                  top: { style: 'single', size: 1, color: 'CCCCCC' },
                  bottom: { style: 'single', size: 1, color: 'CCCCCC' },
                  left: { style: 'single', size: 1, color: 'CCCCCC' },
                  right: { style: 'single', size: 1, color: 'CCCCCC' },
                },
              }));
              codeBlockContent = [];
              inCodeBlock = false;
            } else {
              // Start code block
              inCodeBlock = true;
            }
            continue;
          }

          if (inCodeBlock) {
            codeBlockContent.push(line);
            continue;
          }

          // Handle tables
          if (line.includes('|') && line.trim().length > 0) {
            if (!inTable) {
              inTable = true;
              tableRows = [];
            }
            const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
            if (cells.length > 0 && !cells.every(cell => cell.match(/^[-:|\s]*$/))) {
              tableRows.push(cells);
            }
            continue;
          } else if (inTable) {
            // End table and create proper Word table
            processTableRows();
          }

          // Handle lists
          if (line.match(/^\s*[-*+]\s/) || line.match(/^\s*\d+\.\s/)) {
            const listItemText = line.replace(/^\s*[-*+\d.]\s*/, '');
            currentListItems.push(listItemText);
            continue;
          } else if (currentListItems.length > 0) {
            // End list
            processListItems();
          }

          // Handle headings
          if (line.startsWith('#')) {
            const level = line.match(/^#+/)?.[0].length || 1;
            const headingText = line.replace(/^#+\s*/, '');
            const fontSize = Math.max(16, 36 - (level * 4)) * 2; // Convert to half-points
            
            elements.push(new Paragraph({
              children: [
                new TextRun({
                  text: headingText,
                  bold: true,
                  size: fontSize,
                }),
              ],
              spacing: { before: 400, after: 200 },
            }));
            continue;
          }

          // Handle bold and italic formatting
          if (line.trim().length > 0) {
            // Simple approach: completely strip all markdown symbols and just use plain text
            const cleanText = cleanMarkdownText(line);

            elements.push(new Paragraph({
              children: [
                new TextRun({
                  text: cleanText,
                  size: 24,
                }),
              ],
              spacing: { before: 100, after: 100 },
            }));
          } else {
            // Empty line
            elements.push(new Paragraph({
              children: [new TextRun({ text: "", size: 24 })],
              spacing: { before: 100, after: 100 },
            }));
          }
        }

        // Handle any remaining lists and tables
        processListItems();
        if (inTable) {
          processTableRows();
        }

        return elements;
      };

      // Create a new document with parsed markdown
      const contentElements = parseMarkdownToWordElements(lastSystemMessage.text);
      
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "AI Assistant Response",
                  bold: true,
                  size: 32, // 16pt font
                }),
              ],
              spacing: { after: 200 },
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated on: ${new Date().toLocaleString()}`,
                  size: 20, // 10pt font
                  italics: true,
                }),
              ],
              spacing: { after: 400 },
            }),
            ...contentElements
          ],
        }],
      });

      // Generate the document blob directly (browser-compatible)
      const blob = await Packer.toBlob(doc);
      
      const fileName = `AI_Response_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.docx`;
      saveAs(blob, fileName);
      
    } catch (error) {
      console.error('Error exporting to Word:', error);
      setExportError('Failed to export to Word document. Please try again.');
    }
  };

  // Render helper functions
  const renderCitations = (citations?: Citation[]) => {
    if (!citations || citations.length === 0) return null;

    return (
      <Box className="chat-citations-container" sx={{ mt: 2, p: 2, borderLeft: '3px solid #e0e0e0', backgroundColor: '#fafafa' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#333', fontSize: '0.95rem' }}>
          Sources & References:
        </Typography>
        <Stack spacing={1.5}>
          {citations.map((citation, index) => (
            <Box key={citation.id || index} sx={{ pb: 1, borderBottom: index < citations.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.9rem',
                  color: '#2c5aa0',
                  fontWeight: 'bold',
                  mb: 0.5,
                  lineHeight: 1.4
                }}
              >
                [{citation.citationNumber}] {citation.title}
              </Typography>            
          
              {citation.snippet && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem',
                    color: '#555',
                    lineHeight: 1.5,
                    pl: 1,
                    borderLeft: '2px solid #ddd',
                    backgroundColor: '#f8f9fa',
                    p: 1,
                    borderRadius: '4px',
                    fontStyle: 'italic'
                  }}
                >
                  "{citation.snippet}"
                </Typography>
              )}
              
              {citation.url && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem',
                    color: '#1976d2',
                    mt: 0.5,
                    wordBreak: 'break-all'
                  }}
                >
                  <a 
                    href={citation.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: 'inherit', 
                      textDecoration: 'underline',
                      fontSize: '0.8rem'
                    }}
                  >
                    {citation.url}
                  </a>
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderChatMessages = () => (
    <div className="chat-messages">
      {messages.map((message, index) => {
        console.log(`Message ${index}:`, message);
        return (
          <Box 
            key={index} 
            className={`chat-messages-container ${message.isUser ? 'chat-message-user' : 'chat-message-system'}`}
          >
            <div className="chat-message-content">
              <Markdown remarkPlugins={[remarkGfm]}>{message.text}</Markdown>
              {!message.isUser && renderCitations(message.citations)}
            </div>
          </Box>
        );
      })}    
    </div>
  );

  const renderInputSection = () => (
    <Box className="chat-input-section">
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Type your question here"
        variant="outlined"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        className="chat-input-field"
      />
      <Box className="chat-buttons-container">
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={isLoading}
          className="chat-button"
        >
          Send
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          endIcon={<DownloadIcon />}
          onClick={handleExportToWord}
          disabled={isLoading || messages.filter(msg => !msg.isUser).length === 0}
          className="chat-button"
        >
          Export
        </Button>

      </Box>
    </Box>
  );

  const renderControlsSection = () => (
    <Stack 
      direction="row" 
      spacing={2} 
      className="chat-controls-section"
    >
      <FormControl className="chat-form-control" error={!!companyError}>
        <InputLabel id="company-select-label">Company</InputLabel>
        <Select
          labelId="company-select-label"
          value={selectedCompany?.id || ''}
          label="Company"
          disabled={isCompanyLocked}
          onChange={(e) => {
            const companyId = e.target.value as number;
            const company = companies.find(c => c.id === companyId);
            if (company) {
              setSelectedCompany(company);
              setCompanyError(''); // Clear error when company is selected
            }
          }}
        >
          <MenuItem value="" disabled>
            <em>Select Company</em>
          </MenuItem>
          {companies.map((company) => (
            <MenuItem 
              key={company.id} 
              value={company.id}
            >
              {company.company}
            </MenuItem>
          ))}
        </Select>
        {companyError && (
          <Typography 
            color="error" 
            variant="caption" 
            sx={{ 
              mt: 0.5,
              fontSize: '0.75rem',
              lineHeight: '1.2'
            }}
          >
            {companyError}
          </Typography>
        )}
      </FormControl>
      
      <Box className="chat-execution-mode-container">
        <Typography variant="subtitle1" className="chat-execution-mode-title">
          Execution Mode
        </Typography>
        <RadioGroup
          value={executionMode}
          onChange={(e) => setExecutionMode(e.target.value as ExecutionMode)}
        >
          <FormControlLabel 
            value="Chat" 
            control={<Radio size="small" />} 
            label={<Typography className="chat-execution-mode-label">Chat</Typography>} 
          />
          <FormControlLabel 
            value="Memo Gen" 
            control={<Radio size="small" />} 
            label={<Typography className="chat-execution-mode-label">Memo Gen</Typography>} 
          />
        </RadioGroup>
      </Box>
      
      <FormControl className="chat-form-control">
        <InputLabel>Source</InputLabel>
        <Select
          value={selectedSource}
          label="Source"
          onChange={(e) => setSelectedSource(e.target.value as Source)}
        >
          <MenuItem value="Company Know">Company Knowledge</MenuItem>
          {/* <MenuItem value="World Knowledge">World Knowledge</MenuItem> */}
        </Select>
      </FormControl>
    </Stack>
  );  const renderFileUploadSection = () => (
    <FormControl className="chat-form-control">
      <Box className="chat-file-upload-container">
        <input
          type="file"
          multiple
          accept={ALLOWED_TYPES}
          onChange={handleFileUpload}
          className="chat-file-input-hidden"
          ref={fileInputRef}
          data-testid="file-input"
        />
        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          className="chat-file-upload-button"
        >
          Upload Files
        </Button>
        {selectedFiles.length > 0 && (
          <Box className="chat-file-chips-container">
            {selectedFiles.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => handleRemoveFile(index)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                size="small"
                className="chat-file-chip"
              />
            ))}
          </Box>
        )}
        {fileError && (
          <Typography 
            color="error" 
            variant="caption" 
            sx={{ 
              mt: 1, 
              display: 'block',
              fontSize: '0.75rem',
              lineHeight: '1.2'
            }}
          >
            {fileError}
          </Typography>
        )}
      </Box>
    </FormControl>
  );

  const renderBottomSection = () => (
    <Stack 
      direction="row" 
      spacing={2} 
      className="chat-bottom-section"
    >
      {renderFileUploadSection()}
      
      {executionMode === 'Memo Gen' && (
        <FormControl className="chat-form-control">              
          <InputLabel>Prompt Name</InputLabel>
          <Select
            value={selectedMemoPrompt?.id || ''}
            label="Prompt Name"
            onChange={(e) => {
              const promptId = e.target.value as number;
              const prompt = prompts.find(p => p.id === promptId);
              setSelectedMemoPrompt(prompt || null);
            }}
          >
            <MenuItem value="" disabled>
              <em>Select Prompt</em>
            </MenuItem>
            {prompts.map((prompt) => (
              <MenuItem 
                key={prompt.id} 
                value={prompt.id}
              >
                {prompt.promptName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );

  return (
    <Box className="chat-container">
      <ChatHistory 
        ref={chatHistoryRef}
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
        onConversationSelect={handleConversationSelect}
        onNewChat={handleNewChat}
        currentConversationId={currentConversationId}
      />
      <Box className="chat-main-content">
        {!chatStarted ? (
          <Typography variant="h6" className="chat-welcome-text">Welcome</Typography>
        ) : (
          renderChatMessages()
        )}
        
        {renderInputSection()}
        {renderControlsSection()}
        {renderBottomSection()}
      </Box>
      
      {/* Full screen loading overlay */}
      {isLoading && (
        <Box className="chat-loading-overlay" data-testid="loading-overlay">
          <CircularProgress size={40} />
          <Typography className="chat-loading-text">
            Processing your request...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Chat;
